import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["contentAfter", "contentBefore", "className", "value", "onChange", "clientId", "variant", "type", "leftIcon", "rightIcon", "onFocus", "onBlur", "onKeyDown", "onKeyUp", "validationState", "isDisabled"];
import { useIsComposingHandler, warnOnce } from "@smartsheet/lodestar-utils";
import { useEnsuredForwardedRef } from "@smartsheet/lodestar-utils";
import { useCombinedThemeMode } from "@smartsheet/sds-theme-internal";
import React, { forwardRef, useState } from "react";
import { noop } from "../../constants/noop";
import { StyledContentAfterContainer } from "./styled/StyledContentAfterContainer";
import { StyledContentBeforeContainer } from "./styled/StyledContentBeforeContainer";
import { StyledInput } from "./styled/StyledInput";
import { StyledInputContainer } from "./styled/StyledInputContainer";
import { TextInputRootBox } from "./styled/TextInputRootBox";
import { TextInputRootUnderline } from "./styled/TextInputRootUnderline";

/**
 * Used to provide the left and right customizations access to the current state of the TextInput.
 */

// Omitting "style" because, it's ambiguous as to which sub element
// the style should go on (the input itself or the wrapper).
// Omitting "defaultValue" because we're only support controlled use
// cases right now.
var warnOnDeprecatedDisabledProp = warnOnce("Please use the `isDisabled` prop for <TextInput/> instead of `disabled`. The `disabled` prop has been deprecated due to its issues with a11y.");
var warnOnDeprecatedLeftIconProp = warnOnce("Please use the `contentBefore` prop for <TextInput/> instead of `leftIcon`. The `leftIcon` prop has been deprecated for better RTL better support.");
var warnOnDeprecatedRightIconProp = warnOnce("Please use the `contentAfter` prop for <TextInput/> instead of `rightIcon`. The `rightIcon` prop has been deprecated for better RTL better support.");
export var TextInput = /*#__PURE__*/forwardRef(function (props, parentRef) {
  var contentAfter = props.contentAfter,
    contentBefore = props.contentBefore,
    className = props.className,
    value = props.value,
    onChange = props.onChange,
    clientId = props.clientId,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? "box" : _props$variant,
    _props$type = props.type,
    type = _props$type === void 0 ? "text" : _props$type,
    leftIcon = props.leftIcon,
    rightIcon = props.rightIcon,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    onKeyDown = props.onKeyDown,
    onKeyUp = props.onKeyUp,
    validationState = props.validationState,
    isDisabled = props.isDisabled,
    htmlAttributes = _objectWithoutProperties(props, _excluded);
  var themeMode = useCombinedThemeMode();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isFocused = _useState2[0],
    setIsFocused = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isHovered = _useState4[0],
    setIsHovered = _useState4[1];

  // TODO: remove this after deprecation, use just the `isDisabled` prop.
  var reallyDisabled = isDisabled !== undefined ? isDisabled : htmlAttributes.disabled ? htmlAttributes.disabled : false;

  // TODO: Remove this in https://git.lab.smartsheet.com/team-ui-engineering/lodestar-core/-/issues/516
  var realContentBefore = contentBefore !== undefined ? contentBefore : leftIcon;
  var realContentAfter = contentAfter !== undefined ? contentAfter : rightIcon;
  if (process.env.NODE_ENV !== "production") {
    if (htmlAttributes.disabled !== undefined) {
      warnOnDeprecatedDisabledProp();
    }
    if (leftIcon !== undefined) {
      warnOnDeprecatedLeftIconProp();
    }
    if (rightIcon !== undefined) {
      warnOnDeprecatedRightIconProp();
    }
  }
  var textInputRef = useEnsuredForwardedRef(parentRef);
  function handleOnChange(e) {
    onChange(e.target.value, e);
  }
  function handleOnFocus(e) {
    setIsFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  }
  function handleOnBlur(e) {
    setIsFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  }
  var handleKeyDown = useIsComposingHandler(onKeyDown || noop);
  var handleKeyUp = useIsComposingHandler(onKeyUp || noop);
  var OuterComponent = variant === "box" ? TextInputRootBox : TextInputRootUnderline;
  return /*#__PURE__*/React.createElement(OuterComponent, {
    isDisabled: reallyDisabled,
    isFocused: isFocused,
    isHovered: isHovered,
    validationState: validationState,
    className: className,
    themeMode: themeMode,
    hasContentAfter: !!realContentAfter,
    onMouseEnter: function onMouseEnter() {
      return setIsHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setIsHovered(false);
    },
    onClick: function onClick() {
      var _textInputRef$current;
      (_textInputRef$current = textInputRef.current) === null || _textInputRef$current === void 0 ? void 0 : _textInputRef$current.focus();
    }
  }, realContentBefore && /*#__PURE__*/React.createElement(StyledContentBeforeContainer, {
    themeMode: themeMode,
    validationState: validationState
  }, typeof realContentBefore === "function" ? realContentBefore({
    isFocused: isFocused,
    isHovered: isHovered
  }) : realContentBefore), /*#__PURE__*/React.createElement(StyledInputContainer, null, /*#__PURE__*/React.createElement(StyledInput, _extends({
    variant: variant,
    themeMode: themeMode,
    type: type,
    value: value,
    onChange: handleOnChange,
    ref: textInputRef,
    "data-client-id": clientId,
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    onKeyDown: onKeyDown && handleKeyDown || undefined,
    onKeyUp: onKeyUp && handleKeyUp || undefined,
    isHovered: isHovered,
    "aria-disabled": reallyDisabled
  }, htmlAttributes, {
    readOnly: reallyDisabled ? true : htmlAttributes.readOnly ? htmlAttributes.readOnly : false,
    hasContentAfter: !!realContentAfter,
    hasContentBefore: !!realContentBefore,
    validationState: validationState
  }))), realContentAfter && /*#__PURE__*/React.createElement(StyledContentAfterContainer, {
    themeMode: themeMode,
    validationState: validationState
  }, typeof realContentAfter === "function" ? realContentAfter({
    isFocused: isFocused,
    isHovered: isHovered
  }) : realContentAfter));
});
TextInput.displayName = "TextInput";
//# sourceMappingURL=TextInput.js.map